import * as numeric from 'Helper/numericHelpers'
import debug from 'Helper/debugHelpers'
import globalConfig from 'Helper/configHelpers'
import { select, call, put } from 'redux-saga/effects'
import { delay } from 'Helper/customReduxEffects.js'
import checkOkToProceed from './checkOkToProceed'
import APIstream from './APIstream'
import CRUDcall from './CRUDcall'
import dataManager from './dataManagerSagas'

// Note, more details on gen2's handling of api calls and callbacks can be found
// at https://github.com/XtractSolutions/Gen2_XIS/wiki/xtractAPI_documentation

const configSectionFix2of2 = setup => {
  // there used to be two ways of refering to dashboard, the code is being converted to only use
  // nurseDashboard but databases haven't been migrated yet so the data must be saved as 'dashboard'.
  // once migrations correct databases this fix function (and 1of2) can be removed.
  const { resourceName, obj } = setup
  if (resourceName.toLowerCase() === 'config' && obj?.section === 'nurseDashboard') {
    setup.obj.section = 'dashboard'
  }
  return setup
}

// the standard method used to make api calls in the gen2 framework
export default function * doXtractAPI(action) {
  yield call(checkOkToProceed, action)
  let setup = yield call(APIsetup, action)
  setup = configSectionFix2of2(setup)
  if (action.method.toUpperCase() === 'STREAM') {
    return yield call(APIstream, setup)
  } else {
    return yield call(CRUDcall, setup)
  }
}

// common setup work that needs to be done for both api calls and stream calls.
// parsing the urls figuring out if refresh is in progress, assigning random
// transaction numbers if they aren't present for forensic purposes etc.
export function * APIsetup(action) {
  const auth = yield select(state => state.auth)
  const callers = yield select(state => state.api).callers || []

  if (
    action.config &&
    action.config.windowRef &&
    callers.indexOf(action.config.windowRef) == -1
  ) {
    // track this caller so it can cancel its callbacks if needed.
    yield put({ type: 'ADD_API_CALLER', ref: action.config.windowRef })
  }
  const { obj, config = {} } = action
  let { method, resource, id = null, transaction } = action
  const { API_FAIL_COUNT } = auth
  let { token, refreshInProgress } = auth
  const { api_base_url: API_BASE_URL } = globalConfig

  // this is to parse the id from the resource in the event this call was made with xtractAPI.
  const [resourcePath, queryString] = resource.split('?')
  const resourceArr = resourcePath.split('/')
  const possibleId = resourceArr.pop()

  id = numeric.isNumeric(possibleId) ? possibleId : id
  if (numeric.isNumeric(possibleId)) {
    // if there was an id on the end, we need to rebuild the resource now that the id has been removed
    resource = resourceArr.join('/') + (queryString ? `?${queryString}` : '')
  }

  let messageDisplayed = false
  while (refreshInProgress) {
    if(messageDisplayed === false) {
      messageDisplayed = true
      debug.log(
        `${method} call to ${resource} paused waiting on token refresh (2)`,
        debug.logLevels.API
      )
    }
    // If there is a refresh in progress, delay this call 200ms and then check again
    yield delay(200)
    const { auth = {} } = yield select(state => state.auth)
    const {
      refreshInProgress: currentlyRefreshing = false,
      token: newToken = ''
    } = auth
    if (!currentlyRefreshing) {
      // if the refresh is done, update the auth details and break the loop
      token = newToken
      debug.log(
        `${method} call to ${resource} released after token refresh (2)`,
        debug.logLevels.API
      )
      break
    }
    refreshInProgress = currentlyRefreshing
  }

  // enforce that read without an id is really a read all and read_all with
  // an id is really a read.
  if (method != null && method.toUpperCase() === 'READ' && id == null) {
    method = 'READ_ALL'
  } else if (
    method != null &&
    method.toUpperCase() === 'READ_ALL' &&
    id != null
  ) {
    method = 'READ'
  }

  if(typeof config?.sequentialControllerRef?.current?.abort === "function") {
    // abort previous request
    config.sequentialControllerRef.current.abort('Canceled due to submission of updated api call')
  }

  if (config?.sequentialControllerRef) {
    // we are doing sequential call handling so create an abort controller
    // in case this request needs to be canceled
    config.sequentialControllerRef.current = new AbortController()
  }

  if (
    resource.indexOf('transaction_id=') == -1 &&
    config.exclude_transaction !== true &&
    (method == null || method.toUpperCase() !== 'stream') &&
    transaction == null
  ) {
    // if there was no transaction_id query arg on the api call, add one in.
    // A UUID v4 is globally unique across the single-tenant fleet, so it acts
    // as a correlation key for logs, DB rows, and SQL comments downstream -
    // not just for spotting duplicate deliveries of the same call.
    const transactionId = crypto.randomUUID()
    if (resource.indexOf('?') == -1) {
      transaction = `?transaction_id=${transactionId}`
    } else {
      transaction = `&transaction_id=${transactionId}`
    }
  }

  const { resourceName, idsObj } = prepStoreAction(resource, method, id)

  if (
    method &&
    method.toUpperCase() !== 'READ' &&
    method.toUpperCase() !== 'READ_ALL' &&
    method.toUpperCase() !== 'SEARCH' &&
    method.toUpperCase() !== 'SEARCH_PARTIAL' &&
    resource.split('/').pop().toUpperCase() !== 'LOCK'
  ) {
    // if the call is a create/update/delete then we pass the details to the data manager to determine if there is any
    // data currently in redux that may need to be refreshed based on the changes that this call resulted in. Read
    // calls wont have changed anything so they can bypass this step.
    try {
      yield * dataManager({
        resource: resourceName + '_PRE_API',
        method: method.toUpperCase(),
        ids: idsObj
      })
    } catch (error) {
      debug.log('data manager error: ' + error, debug.logLevels.API, 'error')
    }
  }

  return {
    API_BASE_URL,
    API_FAIL_COUNT,
    token,
    resource,
    method: method ? method.toUpperCase() : null,
    id,
    obj,
    transaction,
    resourceName,
    idsObj,
    action: { ...action, transaction, downlink: config.downlink }
  }
}

// results from api calls are stored systematically based on the URL that was
// called. This function is used to manipulate the URL in order to automatically
// prepare the action associated with any given url.
export function prepStoreAction(resource, method, id = null) {
  // every successful call results in an action being despatched
  // in the format STORE_CLINIC_SEARCH with the result of the action
  // attached. The reducers can subscribe or ignore these actions as
  // necessary. If it is a patient specific call (eg patient/2108/flags)
  // then the patient id is removed from the store call (eg patient_flags)
  // Note that, read, with a non null id equates to a read one
  // action and is dispatched as READ. Read with a null id equates
  // to a read many action and is dispatched as READ_ALL
  const resourceName = resource.split('?')[0].toUpperCase().replaceAll(/\/[0-9]*\//g, '_').replace(/\/[0-9]*$/, '')

  // Create an object of ids from the resource URL to pass in case the reducer needs to know any
  // of them when storing the data.
  const fullUrl = id == null ? resource : `${resource}/${id}`
  const ids = fullUrl.toUpperCase().split('/')

  const length = ids.length % 2 === 0 ? ids.length : ids.length - 1
  const idsObj = {}

  for (let i = 0; i < length; i = i + 2) {
    idsObj[`${ids[i].toLowerCase()}_id`] = ids[i + 1]
  }
  return { resourceName, method, idsObj }
}
